import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "@/shared/i18n";
import { Button } from "@/shared/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { CampaignForm } from "../ui/CampaignForm";
import { useCampaign, useCreateCampaign, useUpdateCampaign } from "../model/hooks";
import type { CreateCampaignPayload, UpdateCampaignPayload } from "../types";

export function CampaignFormPage() {
  const { t } = useTranslation("bulk-notifications");
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;

  const { campaign, isLoading: isLoadingCampaign } = useCampaign(id ?? null);
  const { createCampaign, isLoading: isCreating } = useCreateCampaign();
  const { updateCampaign, isLoading: isUpdating } = useUpdateCampaign();

  const isLoading = isCreating || isUpdating;
  const isLoadingData = !isNew && isLoadingCampaign;

  const handleSubmit = async (payload: CreateCampaignPayload) => {
    if (isNew) {
      await createCampaign(payload);
      navigate("/notifications/campaigns");
    } else if (id) {
      const updatePayload: UpdateCampaignPayload = {
        ...payload,
      };
      await updateCampaign({ id, payload: updatePayload });
      navigate("/notifications/campaigns");
    }
  };

  const handleCancel = () => {
    navigate("/notifications/campaigns");
  };

  if (!isNew && isLoadingData) {
    return (
      <div className="creator-financial-page -m-6 space-y-8 p-8 pb-8">
        <Button
          variant="outline"
          className="cf-btn-outline text-[var(--cf-text)]"
          onClick={() => navigate("/notifications/campaigns")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
          {t("back")}
        </Button>
        <div className="cf-card">
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-[var(--cf-text-muted)] mb-4" aria-hidden />
            <p className="text-sm text-[var(--cf-text-muted)]">{t("loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="creator-financial-page -m-6 space-y-8 p-8 pb-8">
      <Button
        variant="outline"
        className="cf-btn-outline text-[var(--cf-text)]"
        onClick={() => navigate("/notifications/campaigns")}
      >
        <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
        {t("back")}
      </Button>
      <div className="cf-card">
        <h2 className="cf-page-title text-xl mb-1">
          {isNew ? t("newCampaign") : t("editCampaign")}
        </h2>
        <p className="cf-page-subtitle mb-6">
          {isNew ? t("campaignsList") : campaign?.title}
        </p>
        <CampaignForm
          campaign={campaign ?? null}
          isLoading={isLoading}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
}
